/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/AppointmentRegistrationSections/Certifications/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useEffect } from "react";
import styles from "./Certifications.module.css";
import { useTranslation } from "react-i18next";
import {
  getCertificationsOptions,
  getAvailableSectorsOptions,
  getYearsOfExperienceOptions,
  getREPSEProviderOptions,
  getAverageDeliveryTimesOptions,
  getAcceptedPaymentMethodsOptions,
  getCivilLiabilityInsuranceOptions,
  getIMMEXPermitOptions,
} from "../../../data/selectOptions";
import { useFieldValidation } from "../../../hooks/useFieldValidation";
import { useCertificationsSectionState } from "../../../hooks/useCertificationsSectionState.js";
import { FIELD_VALIDATION_ERROR_BORDER_STYLE } from "../../../utils/formValidationConstants.js";
import CertificationCheckboxGrid from "./CertificationCheckboxGrid.jsx";
import SectorCheckboxGrid from "./SectorCheckboxGrid.jsx";
import SectorExperienceGrid from "./SectorExperienceGrid.jsx";
import DocumentedSystemRadioGroup from "./DocumentedSystemRadioGroup.jsx";
import CertificationsPolicyFields from "./CertificationsPolicyFields.jsx";
import CertificationsCapacityField from "./CertificationsCapacityField.jsx";

const TABLET_MAX_WIDTH = 1023;

const Certifications = ({
  company,
  onFieldChange,
  onValidationChange,
  missingRequiredFields,
  getFieldValidation,
}) => {
  const { t } = useTranslation();
  const isTablet = window.innerWidth <= TABLET_MAX_WIDTH;

  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    capacidadInstalada: "",
    clientesPrincipalesYReferenciasComerciales: "",
  });

  const {
    selectedCertifications,
    sectors,
    documentedSystem,
    esProveedorREPSE,
    tiemposPromedioEntrega,
    metodosDePagoAceptados,
    seguroDeResponsabilidadCivil,
    permisoIMMEX,
    prevHasErrorsRef,
    onValidationChangeRef,
    handleCheckboxChange,
    handleSectorCheckboxChange,
    handleSectorExperienceChange,
    handleDocumentedSystemChange,
    handleSelectFieldChange,
    handleInputChange,
    setEsProveedorREPSE,
    setTiemposPromedioEntrega,
    setMetodosDePagoAceptados,
    setSeguroDeResponsabilidadCivil,
    setPermisoIMMEX,
  } = useCertificationsSectionState({
    company,
    onFieldChange,
    onValidationChange,
  });

  const certifications = getCertificationsOptions(t);
  const availableSectors = getAvailableSectorsOptions(t);
  const yearsOptions = getYearsOfExperienceOptions(t);
  const REPSEProviderOptions = getREPSEProviderOptions(t);
  const averageDeliveryTimesOptions = getAverageDeliveryTimesOptions(t);
  const acceptedPaymentMethodsOptions = getAcceptedPaymentMethodsOptions(t);
  const civilLiabilityInsuranceOptions = getCivilLiabilityInsuranceOptions(t);
  const IMMEXPermitOptions = getIMMEXPermitOptions(t);

  useEffect(() => {
    if (
      onValidationChangeRef.current &&
      prevHasErrorsRef.current !== hasErrors
    ) {
      prevHasErrorsRef.current = hasErrors;
      onValidationChangeRef.current(hasErrors);
    }
  }, [hasErrors, onValidationChangeRef, prevHasErrorsRef]);

  const sectorsValidationStyle =
    getFieldValidation && getFieldValidation("sectores")
      ? FIELD_VALIDATION_ERROR_BORDER_STYLE
      : undefined;

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.certifications.title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>

      {company && Object.hasOwn(company, "certificaciones") && (
        <div className={styles.checkboxContainer}>
          <CertificationCheckboxGrid
            certifications={certifications}
            selectedCertifications={selectedCertifications}
            onToggle={handleCheckboxChange}
            getFieldValidation={getFieldValidation}
            missingRequiredFields={missingRequiredFields}
            t={t}
            styles={styles}
          />
        </div>
      )}

      {company && Object.hasOwn(company, "sectores") && (
        <>
          <div className={styles.sectionHeader} style={sectorsValidationStyle}>
            <span className={styles.inputLabel}>
              {t("components.certifications.sectorsAttendedLabel")}
              {missingRequiredFields &&
                Object.hasOwn(missingRequiredFields, "sectores") && (
                  <span className={styles.required}> *</span>
                )}
            </span>
          </div>
          <div
            className={styles.checkboxContainer}
            style={sectorsValidationStyle}
          >
            <SectorCheckboxGrid
              availableSectors={availableSectors}
              sectors={sectors}
              onToggle={handleSectorCheckboxChange}
              styles={styles}
            />
          </div>

          {sectors.length > 0 && (
            <>
              <div className={styles.sectionHeader}>
                <span className={styles.inputLabel}>
                  {t(
                    "components.certifications.yearsOfExperienceInTheSectorLabel",
                  )}
                </span>
              </div>
              <div
                className={`${styles.checkboxContainer} ${styles.sectorsSpacing}`}
              >
                <SectorExperienceGrid
                  sectors={sectors}
                  yearsOptions={yearsOptions}
                  isTablet={isTablet}
                  onExperienceChange={handleSectorExperienceChange}
                  t={t}
                  styles={styles}
                />
              </div>
            </>
          )}
        </>
      )}

      {company && Object.hasOwn(company, "tieneSistemaDocumentado") && (
        <DocumentedSystemRadioGroup
          documentedSystem={documentedSystem}
          onChange={handleDocumentedSystemChange}
          getFieldValidation={getFieldValidation}
          missingRequiredFields={missingRequiredFields}
          isTablet={isTablet}
          t={t}
          styles={styles}
        />
      )}

      <CertificationsCapacityField
        company={company}
        t={t}
        styles={styles}
        isTablet={isTablet}
        missingRequiredFields={missingRequiredFields}
        getFieldValidation={getFieldValidation}
        getFieldValidationState={getFieldValidationState}
        getErrorMessage={getErrorMessage}
        onInputChange={handleInputChange}
        onValidatedFieldChange={handleFieldChange}
      />

      <CertificationsPolicyFields
        company={company}
        t={t}
        styles={styles}
        REPSEProviderOptions={REPSEProviderOptions}
        averageDeliveryTimesOptions={averageDeliveryTimesOptions}
        acceptedPaymentMethodsOptions={acceptedPaymentMethodsOptions}
        civilLiabilityInsuranceOptions={civilLiabilityInsuranceOptions}
        IMMEXPermitOptions={IMMEXPermitOptions}
        esProveedorREPSE={esProveedorREPSE}
        tiemposPromedioEntrega={tiemposPromedioEntrega}
        metodosDePagoAceptados={metodosDePagoAceptados}
        seguroDeResponsabilidadCivil={seguroDeResponsabilidadCivil}
        permisoIMMEX={permisoIMMEX}
        onSelectFieldChange={handleSelectFieldChange}
        setEsProveedorREPSE={setEsProveedorREPSE}
        setTiemposPromedioEntrega={setTiemposPromedioEntrega}
        setMetodosDePagoAceptados={setMetodosDePagoAceptados}
        setSeguroDeResponsabilidadCivil={setSeguroDeResponsabilidadCivil}
        setPermisoIMMEX={setPermisoIMMEX}
        getFieldValidation={getFieldValidation}
        missingRequiredFields={missingRequiredFields}
      />
    </div>
  );
};

export default Certifications;
